import { useCallback, useEffect, useMemo } from "react";

import {
  ContractTypeAwsPpa,
  ContractTypeBettercloud,
  ContractTypeGoogleCloudPartnerLedPremiumSupport,
  ContractTypeLooker,
  type ContractTypeOption,
  ContractTypeZendesk,
  DoitRole,
} from "@doitintl/cmp-models";
import { Autocomplete, Stack, TextField } from "@mui/material";

import { useDoitRoleCheck } from "../../../../../../Components/hooks/useDoitRoles";
import { useContractsContext } from "../../../../../../Context/customer/ContractsContext";
import { useContractTypesContext } from "../../../../../ContractsTabs/ContractsTypesContext";
import { fieldRange } from "../../../const";
import { stateDefaultValues, useContractFormContext } from "../../../ContractsFormContext";
import { type ContractStateType, type ContractType } from "../../../types";
import { ifProductWithoutEntity, isGoogleCloudPartnerLedPremiumSupport } from "../../../utils";

export const ContractTypeSelector = () => {
  const { state, setState } = useContractFormContext();
  const isDoitContractAdmin = useDoitRoleCheck(DoitRole.ContractAdmin);
  const isDoitContractOwner = useDoitRoleCheck(DoitRole.ContractOwner);
  const { contractProductTypes, contractProductSubtypes } = useContractTypesContext();

  const handleChangeContractType = useCallback(
    (_, selectedValue: ContractTypeOption) => {
      const selectedType = selectedValue?.value as ContractType;
      setState((prevState) => {
        const update: ContractStateType = {
          ...stateDefaultValues,
          type: selectedType,
          entity: prevState.entity,
          accountManager: prevState.accountManager,
          subtype: "",
          errors: {
            ...prevState.errors,
            type: false,
            subtype: false,
            discountEndDate: false,
            chargePerTerm: false,
          },
        };

        if ([ContractTypeBettercloud, ContractTypeZendesk].includes(selectedType)) {
          update.isCommitment = true;
        }

        if (ContractTypeGoogleCloudPartnerLedPremiumSupport === selectedType) {
          if (isGoogleCloudPartnerLedPremiumSupport(update)) {
            update.plpsPercent = fieldRange.plpsPercent.default;
            return { ...prevState, ...update };
          }
        }

        if ("pointOfSale" in state && state.pointOfSale) {
          if (ifProductWithoutEntity(selectedType, state.pointOfSale)) {
            update.entity = "";
            update.errors = { ...update.errors, entity: false };
          }
        }

        return { ...prevState, ...update };
      });
    },
    [setState, state]
  );

  const handleChangeContractSubtype = useCallback(
    (_, selectedValue: ContractTypeOption) => {
      setState((prevState) => ({
        ...prevState,
        subtype: selectedValue?.value,
        errors: { ...prevState.errors, subtype: false },
      }));
    },
    [setState]
  );

  const { contracts } = useContractsContext();

  const hasValidLookerContracts = useMemo(() => {
    const filteredLookerContracts = contracts.filter((contract) => contract.type === ContractTypeLooker);

    return filteredLookerContracts.some((contract) => {
      const currentDate = new Date();
      const contractStartDate = contract?.startDate?.toDate();

      if (contractStartDate) {
        const longestSkuMonths = contract.properties?.skus?.reduce(
          (longest, sku) => (sku.months > longest ? sku.months : longest),
          0
        );

        if (longestSkuMonths === undefined) {
          return false;
        }
        const contractEndDate = new Date(contractStartDate);
        contractEndDate.setMonth(contractEndDate.getMonth() + longestSkuMonths);

        currentDate.setHours(0, 0, 0, 0);
        contractEndDate.setHours(0, 0, 0, 0);

        return contractEndDate > currentDate;
      }

      return false;
    });
  }, [contracts]);

  useEffect(() => {
    if (!state.type) {
      setState({ ...state, type: contractProductTypes[0].value as ContractType });
    }
  }, [contractProductTypes, setState, state, state.type]);

  return (
    <Stack spacing={1.5}>
      <Autocomplete
        id="contract-type"
        data-cy="contract-type"
        blurOnSelect
        getOptionDisabled={(option) =>
          option.value === ContractTypeLooker && hasValidLookerContracts && !isDoitContractAdmin && !isDoitContractOwner
        }
        disableClearable
        options={contractProductTypes}
        value={contractProductTypes.find((o) => o.value === state.type) ?? contractProductTypes[0]}
        onChange={handleChangeContractType}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        disabled={state.editMode}
        getOptionLabel={(option) =>
          option.value === ContractTypeLooker && hasValidLookerContracts
            ? `${option.label} (Contract already exists, Add SKUs to existing contract)`
            : option.label
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Product type"
            helperText="Select the contract's product type"
            error={state.errors.type}
            fullWidth
          />
        )}
      />
      {state.type === ContractTypeAwsPpa && (
        <Autocomplete
          id="contract-subtype"
          data-cy="contract-subtype"
          blurOnSelect
          disableClearable
          options={contractProductSubtypes}
          value={contractProductSubtypes.find((o) => o.value === state.subtype) ?? { label: "", value: "" }}
          onChange={handleChangeContractSubtype}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          disabled={state.editMode}
          renderInput={(params) => (
            <TextField
              {...params}
              label="PPA type"
              helperText="Select PPA type"
              error={state.errors.subtype}
              fullWidth
            />
          )}
        />
      )}
    </Stack>
  );
};
